import {
  forwardRef,
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
} from 'react';

export interface AnimatedDialogProps extends PropsWithChildren {
  id?: string;
  /** Animation duration */
  duration: number;
  /** Whether the dialog should be shown */
  open?: boolean;
  /** Additional class */
  className?: string;
  /** On toggle event handler */
  onToggle?: (state: boolean) => void;
  /** Optional role */
  role?: string;
}

/** An animated dialog element */
export const AnimatedDialog = forwardRef(
  (
    {
      duration = 500,
      children,
      open,
      className,
      onToggle,
      ...props
    }: AnimatedDialogProps,
    ref,
  ) => {
    const _dialogRef = useRef<HTMLDialogElement>(null);
    const dialog =
      (ref as React.MutableRefObject<HTMLDialogElement | null>) || _dialogRef;

    const timer = useRef<number | null>(null);

    const [state, setState] = useState({
      isOpen: open,
      isClosing: false,
      isOpening: false,
    });

    const doClose = () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }

      setState({
        isOpen: false,
        isClosing: true,
        isOpening: false,
      });

      if (onToggle) {
        onToggle(false);
      }
    };

    const doOpen = () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }

      setState({
        isOpen: true,
        isOpening: true,
        isClosing: false,
      });

      if (onToggle) {
        onToggle(true);
      }
    };

    const onAnimationFinish = (open: boolean) => {
      if (!dialog.current) {
        return;
      }

      setState({
        isOpen: open,
        isClosing: false,
        isOpening: false,
      });
    };

    useEffect(() => {
      if (dialog.current) {
        window.requestAnimationFrame(() => {
          if (state.isClosing) {
            dialog.current?.getBoundingClientRect();
            dialog.current?.classList.remove('open');
            timer.current = window.setTimeout(
              () => onAnimationFinish(false),
              duration,
            );
          } else if (state.isOpening) {
            dialog.current?.getBoundingClientRect();
            dialog.current?.classList.add('open');
            timer.current = window.setTimeout(
              () => onAnimationFinish(true),
              duration,
            );
          }
        });
      }

      return () => {
        if (timer.current != null) {
          window.clearTimeout(timer.current);
          timer.current = null;
        }
      };
    });

    // State managed by parent
    if (open !== null) {
      if (!open && state.isOpen) {
        doClose();
      } else if (open && !state.isOpen) {
        doOpen();
      }
    }

    return (
      <dialog
        ref={dialog}
        className={
          className +
          ((state.isOpen && !state.isOpening) || state.isClosing ? ' open' : '')
        }
        open={state.isOpen || state.isClosing || undefined}
        {...props}
      >
        {children}
      </dialog>
    );
  },
);
